<template>
  <div class="col">
    <router-link
      to="/marketing/price-comparison"
      class="btn btn-primary btn-no-r"
    >
      <span><BaseIcon name="wallet-money" /> {{ $t("menu.priceLogs") }}</span>
    </router-link>
    <router-link
      to="/marketing/price-comparison/data"
      class="btn btn-no-l btn-secondary"
    >
      <span
        ><BaseIcon name="info-circle" /> {{ $t("menu.priceComparison") }}</span
      >
    </router-link>
  </div>
  <div class="price-logs-dashboard">
    <div class="row mb-4 align-items-center">
      <div class="col">
        <p class="text-muted small">
          Monitor real-time price changes and competitor alerts
        </p>
      </div>
      <div class="col-auto"></div>
    </div>

    <!-- Stats Overview -->
    <div class="row mb-4">
      <div class="col-md-3">
        <div class="stat-card">
          <div class="stat-icon bg-soft-primary">
            <BaseIcon name="chart-3" color="var(--primary)" />
          </div>
          <div class="stat-content">
            <span class="stat-label">Total Events</span>
            <h3 class="stat-value">{{ total }}</h3>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="stat-card">
          <div class="stat-icon bg-soft-success">
            <BaseIcon name="trend-up" color="var(--success)" />
          </div>
          <div class="stat-content">
            <span class="stat-label">Price Changes</span>
            <h3 class="stat-value text-success">{{ priceChangesCount }}</h3>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="stat-card">
          <div class="stat-icon bg-soft-danger">
            <BaseIcon name="warning" color="var(--danger)" />
          </div>
          <div class="stat-content">
            <span class="stat-label">Alerts</span>
            <h3 class="stat-value text-danger">{{ alertsCount }}</h3>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="stat-card">
          <div class="stat-icon bg-soft-warning">
            <BaseIcon name="timer" color="var(--warning)" />
          </div>
          <div class="stat-content">
            <span class="stat-label">Last 24h</span>
            <h3 class="stat-value">{{ last24hCount }}</h3>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <div class="activity-container">
          <div
            class="
              activity-header
              d-flex
              justify-content-between
              align-items-center
              mb-3
            "
          >
            <h5 class="m-0">Recent Activity</h5>
            <div class="badge bg-light text-dark">
              {{ items.length }} visible
            </div>
          </div>

          <div v-if="loadInProcess && page === 1" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>

          <div
            v-else-if="items.length === 0"
            class="empty-state text-center py-5"
          >
            <div class="empty-icon mb-3">
              <BaseIcon name="box-search" size="48" color="#ccc" />
            </div>
            <h4>No activity found</h4>
            <p class="text-muted">
              Try adjusting your filters or search query.
            </p>
          </div>

          <div v-else class="activity-list">
            <div
              v-for="log in items"
              :key="log.id"
              class="activity-item shadow-sm"
            >
              <div
                class="activity-type-indicator"
                :class="'bg-' + getLogTypeClass(log)"
              ></div>
              <div class="row align-items-center g-0 p-3">
                <!-- Product Info -->
                <div class="col-md-4">
                  <div class="d-flex align-items-center">
                    <div class="product-img-wrapper me-3">
                      <img
                        v-if="log.product_variant?.product?.image"
                        :src="
                          mediaServer +
                          '/products/' +
                          log.product_variant.product.id +
                          '/default/64-default.webp'
                        "
                        class="product-img"
                      />
                      <div v-else class="product-img-placeholder">
                        <BaseIcon name="no-image" color="#aaa" />
                      </div>
                    </div>
                    <div class="product-info">
                      <h6
                        class="mb-0 text-truncate"
                        style="max-width: 200px"
                        :title="log.product_variant?.product?.name"
                      >
                        {{ log.product_variant?.product?.name }}
                      </h6>
                      <small class="text-muted d-block"
                        >SKU: {{ log.product_variant?.sku }}</small
                      >
                      <small class="badge bg-light text-dark mt-1">{{
                        log.type
                      }}</small>
                    </div>
                  </div>
                </div>

                <!-- Comparison -->
                <div class="col-md-4">
                  <div
                    class="
                      price-comparison-box
                      d-flex
                      align-items-center
                      justify-content-center
                    "
                  >
                    <div class="price-side text-end">
                      <span class="price-label">Old</span>
                      <div class="price-val">
                        {{ formatPrice(log.old_price, log.currency_code) }}
                      </div>
                    </div>
                    <div class="price-arrow mx-3">
                      <BaseIcon
                        :name="getPriceTrendIcon(log)"
                        :color="getPriceTrendColor(log)"
                      />
                    </div>
                    <div class="price-side text-start">
                      <span class="price-label">New</span>
                      <div
                        class="price-val"
                        :class="getPriceTrendTextClass(log)"
                      >
                        {{ formatPrice(log.new_price, log.currency_code) }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Competitor & Reason -->
                <div class="col-md-4">
                  <div class="additional-info ps-3 border-start">
                    <div
                      v-if="log.competitor_price"
                      class="competitor-info mb-1"
                    >
                      <small class="text-muted">Competitor: </small>
                      <span class="fw-bold">{{
                        formatPrice(log.competitor_price, log.currency_code)
                      }}</span>
                    </div>
                    <div
                      class="reason-text small text-truncate"
                      :title="log.reason"
                    >
                      {{ log.reason }}
                    </div>
                    <div class="log-time text-muted mt-1 small">
                      <BaseIcon name="clock" size="12" class="me-1" />
                      {{ formatDate(log.created_at) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Load More Trigger -->
            <div v-if="items.length < total" class="text-center mt-4 mb-5">
              <button
                @click="loadMore"
                class="btn btn-outline-primary btn-sm px-4"
                :disabled="loadInProcess"
              >
                <span
                  v-if="loadInProcess"
                  class="spinner-border spinner-border-sm me-2"
                ></span>
                {{ loadInProcess ? "Loading..." : "Load More Activity" }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from "@/modules/http";
import { useStore } from "vuex";
import BaseIcon from "@/components/icons/BaseIcon.vue";
import date from "@/modules/date";
import tenant from "@/modules/tenant";
import dayjs from "dayjs";

export default {
  name: "PriceLogs",
  components: {
    BaseIcon,
  },
  data() {
    return {
      store: useStore(),
      type: "price-logs",
      items: [],
      total: 0,
      page: 1,
      loadInProcess: false,
      filtersTimeOut: null,
      selectedLineOption: 0,
      lineOptions: [],
      tableRows: [],
      tableKey: 0,
      priceChangesCount: 0,
      alertsCount: 0,
      last24hCount: 0,
    };
  },
  computed: {
    q() {
      return this.store.state.topBar.q;
    },
    filterPage() {
      return this.store.state.filterPage;
    },
    filters() {
      return this.store.state.filters?.price_logs;
    },
    date() {
      return this.store.state.topBar.date;
    },
    mediaServer() {
      return tenant.getAppMediaUrl();
    },
  },
  mounted() {
    this.loadData();
    this.loadStats();
  },
  watch: {
    q() {
      if (this.filtersTimeOut !== null) {
        clearTimeout(this.filtersTimeOut);
      }
      this.filtersTimeOut = setTimeout(() => {
        this.filtersTimeOut = null;
        this.items = [];
        this.page = 1;
        this.loadData();
      }, 450);
    },
    filters: {
      deep: true,
      handler() {
        if (this.filterPage !== "price_logs") {
          return;
        }
        if (this.filtersTimeOut !== null) {
          clearTimeout(this.filtersTimeOut);
        }
        this.filtersTimeOut = setTimeout(() => {
          this.filtersTimeOut = null;
          this.items = [];
          this.page = 1;
          this.loadData();
        }, 450);
      },
    },
    date: {
      deep: true,
      handler() {
        this.items = [];
        this.page = 1;
        this.loadData();
        this.loadStats();
      },
    },
  },
  methods: {
    loadData() {
      this.loadInProcess = true;
      let url = `/price-comparison/price-logs?page=${this.page}&per_page=50&q=${
        this.q ? this.q : ""
      }`;

      if (this.date && this.date[0] && this.date[1]) {
        url += `&from=${dayjs(this.date[0]).format("YYYY-MM-DD")}&to=${dayjs(
          this.date[1]
        ).format("YYYY-MM-DD")}`;
      }

      if (
        this.filters &&
        Object.keys(this.filters).length > 0 &&
        this.filterPage === "price_logs"
      ) {
        for (const filter in this.filters) {
          if (Array.isArray(this.filters[filter].value)) {
            for (const value of this.filters[filter].value) {
              url += `&filter[${filter}][]=${value}`;
            }
          } else {
            url += `&filter[${filter}]=${this.filters[filter].value}`;
          }
        }
      }

      http
        .fetch(url)
        .then((response) => {
          const newItems = response.data;
          if (this.page === 1) {
            this.items = newItems;
            this.tableKey++;
          } else {
            this.items = [...this.items, ...newItems];
          }
          this.total = response.total;
          this.loadInProcess = false;
        })
        .catch(() => {
          this.loadInProcess = false;
        });
    },
    loadStats() {
      let url = "/price-comparison/price-logs-stats";
      if (this.date && this.date[0] && this.date[1]) {
        url += `?from=${dayjs(this.date[0]).format("YYYY-MM-DD")}&to=${dayjs(
          this.date[1]
        ).format("YYYY-MM-DD")}`;
      }
      http
        .fetch(url)
        .then((data) => {
          this.priceChangesCount = data.price_changes_count || 0;
          this.alertsCount = data.alerts_count || 0;
          this.last24hCount = data.last_24h_count || 0;
        })
        .catch(() => {
          // Fallback or silent fail
        });
    },
    loadMore() {
      if (this.items.length < this.total) {
        this.page++;
        this.loadData();
      }
    },
    formatDate(val) {
      return date.format(val, true, true);
    },
    formatPrice(amount, currency) {
      if (amount === null || amount === undefined || amount === "") return "-";
      return Number(amount).toLocaleString("hu-HU", {
        style: "currency",
        currency: currency || "HUF",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    getLogTypeClass(log) {
      if (log.type === "alert") return "danger";
      if (log.type === "price_change") return "primary";
      return "secondary";
    },
    getPriceTrendIcon(log) {
      if (!log.old_price || !log.new_price) return "line";
      const oldP = Number(log.old_price);
      const newP = Number(log.new_price);
      if (newP > oldP) return "trend-up";
      if (newP < oldP) return "trend-down";
      return "line";
    },
    getPriceTrendColor(log) {
      if (!log.old_price || !log.new_price) return "#ccc";
      const oldP = Number(log.old_price);
      const newP = Number(log.new_price);
      if (newP > oldP) return "var(--success)";
      if (newP < oldP) return "var(--danger)";
      return "#ccc";
    },
    getPriceTrendTextClass(log) {
      if (!log.old_price || !log.new_price) return "";
      const oldP = Number(log.old_price);
      const newP = Number(log.new_price);
      if (newP > oldP) return "text-success";
      if (newP < oldP) return "text-danger";
      return "";
    },
  },
};
</script>

<style scoped>
.dashboard-title {
  font-weight: 700;
  color: #2d3436;
  margin-bottom: 0.25rem;
}

/* Stat Cards */
.stat-card {
  background: #fff;
  border-radius: 12px;
  padding: 1.25rem;
  display: flex;
  align-items: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.02);
  transition: transform 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
}

.bg-soft-primary {
  background-color: rgba(67, 97, 238, 0.1);
}
.bg-soft-success {
  background-color: rgba(62, 180, 137, 0.1);
}
.bg-soft-danger {
  background-color: rgba(239, 71, 111, 0.1);
}
.bg-soft-warning {
  background-color: rgba(255, 209, 102, 0.1);
}

.stat-label {
  font-size: 0.75rem;
  color: #636e72;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  display: block;
}

.stat-value {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
  color: #2d3436;
}

/* Activity List */
.activity-container {
  margin-top: 1rem;
}

.activity-item {
  background: #fff;
  border-radius: 10px;
  margin-bottom: 1rem;
  position: relative;
  overflow: hidden;
  transition: all 0.2s ease;
}

.activity-item:hover {
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.05) !important;
}

.activity-type-indicator {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
}

.product-img-wrapper {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  overflow: hidden;
  background: #f1f2f6;
  flex-shrink: 0;
}

.product-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-img-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.price-label {
  font-size: 0.65rem;
  text-transform: uppercase;
  color: #b2bec3;
  display: block;
}

.price-val {
  font-weight: 700;
  font-size: 0.9rem;
}

.price-arrow {
  display: flex;
  align-items: center;
  opacity: 0.7;
}

.reason-text {
  color: #636e72;
  font-style: italic;
}

.empty-state {
  background: #fff;
  border-radius: 12px;
  border: 2px dashed #e1e4e8;
}

.empty-icon {
  opacity: 0.3;
}

@media (max-width: 768px) {
  .price-comparison-box {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #eee;
  }
  .additional-info {
    margin-top: 1rem;
    padding-left: 0 !important;
    border-left: none !important;
    border-top: 1px solid #eee;
    padding-top: 1rem;
  }
}
</style>
